import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Space,
  Typography,
  Card,
  Tag,
  Badge,
} from 'antd';
import {
  EditOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { PromptTemplate } from '../../types';
import { promptTemplatesApi } from '../../services/api/promptTemplates';
import { useApi } from '../../hooks';
import { formatDate } from '../../utils';
import { PAGE_SIZE } from '../../constants';
import EditPromptTemplateModal from './EditPromptTemplateModal';

const { Title } = Typography;

const PromptTemplates: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage, setCurrentPage] = useState<number>(() => {
    const page = parseInt(searchParams.get('page') || '1', 10);
    return Number.isNaN(page) || page <= 0 ? 1 : page;
  });

  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const params = new URLSearchParams();
    if (currentPage > 1) params.set('page', String(currentPage));

    setSearchParams(params, { replace: true });
  }, [currentPage, setSearchParams]);

  const getPromptTemplates = useCallback(() => promptTemplatesApi.getPromptTemplates({
    pageIndex: currentPage - 1,
    pageSize: PAGE_SIZE,
  }), [currentPage]);

  const {
    data: templatesData,
    loading: templatesLoading,
    refresh: refreshTemplates,
  } = useApi(getPromptTemplates, { immediate: true });

  useEffect(() => {
    refreshTemplates();
  }, [refreshTemplates, currentPage]);

  const handleEdit = (template: PromptTemplate) => {
    setEditingTemplate(template);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingTemplate(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    refreshTemplates();
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 200,
      render: (code: string) => (
        <Tag color="blue" icon={<FileTextOutlined />}>
          {code}
        </Tag>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 300,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <Badge
          status={isActive ? 'success' : 'default'}
          text={isActive ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      title: 'Variables',
      dataIndex: 'variables',
      key: 'variables',
      width: 120,
      render: (variables: string[] | null) => (
        <span>{variables ? variables.length : 0} vars</span>
      ),
    },
    {
      title: 'Modified At',
      dataIndex: 'modifiedAt',
      key: 'modifiedAt',
      width: 180,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: PromptTemplate) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            Prompt Templates
          </Title>
        </div>

        <Table
          columns={columns}
          dataSource={templatesData?.data || []}
          rowKey="id"
          loading={templatesLoading}
          scroll={{ x: 1200 }}
          pagination={{
            current: currentPage,
            pageSize: PAGE_SIZE,
            total: templatesData?.totalRecord || 0,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} templates`,
          }}
        />
      </Card>

      <EditPromptTemplateModal
        visible={isModalVisible}
        template={editingTemplate}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default PromptTemplates;
